import type { TimelineEventView } from "@nodus/ui";

/** One row of `GET /api/events/member/` (backend `core.api.NodusEventSchema`).
 *
 *  Rows the member sees only through the alarm/location rule come back with
 *  `actor_name` / `tenant_name` blanked and a person-free title — the backend
 *  never names who disarmed a zone on another company's behalf. */
export interface MemberEvent {
  id: number;
  severity: string;
  django_app: string;
  django_model: string;
  actor_name: string;
  title: string;
  description: string;
  location: string;
  event_type: string;
  device_name: string;
  tenant_name: string;
  image?: string | null;
  external_id?: string | null;
  /** The producing source, e.g. `iot.source:26`: one alarm zone's per-floor rows share it. */
  source_key?: string | null;
  /** The source's name; null on rows seen only through the alarm rule. */
  source_name?: string | null;
  created_at: string;
}

/** A page of the member feed. `next_cursor` = ask again with it; `null` = no
 *  more history. A short (even empty) page with a cursor is normal — the
 *  backend's scan budget ran out before it filled the page. */
export interface MemberActivityPage {
  results: MemberEvent[];
  next_cursor: string | null;
}

/** Query-key root for every member-activity query (page + dashboard card), so
 *  one invalidation refreshes both. */
export const MEMBER_ACTIVITY_KEY = ["member-activity"] as const;

/** Debounce for `activity_changed` pings: an alarm walk-through fires a burst
 *  of events (egress, deactivation, zone) within a second or two. */
export const ACTIVITY_REFETCH_DEBOUNCE_MS = 1_500;

/** Slow poll under the realtime ping (the portal's usual safety net). */
export const ACTIVITY_POLL_MS = 60_000;

const orNull = (s: string | null | undefined) => (s && s.trim() ? s : null);

/** Map a member-feed row onto the shared `@nodus/ui` timeline view-model. */
export function toTimelineEventView(event: MemberEvent): TimelineEventView {
  return {
    id: event.id,
    title: event.title,
    severity: (event.severity || "info").toLowerCase(),
    time: event.created_at,
    description: orNull(event.description),
    actorName: orNull(event.actor_name),
    deviceName: orNull(event.device_name),
    location: orNull(event.location),
    kind: { app: orNull(event.django_app), model: orNull(event.django_model), type: orNull(event.event_type) },
    externalId: event.external_id ?? undefined,
    sourceKey: orNull(event.source_key),
    sourceName: orNull(event.source_name),
  };
}

/** Flatten loaded pages into one newest-first list, dropping duplicates (a
 *  refetch can shift a row across a page boundary). */
export function flattenPages(pages: readonly MemberActivityPage[] | undefined): MemberEvent[] {
  const seen = new Set<number>();
  const out: MemberEvent[] = [];
  for (const page of pages ?? []) {
    for (const e of page.results) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}

/** Trailing-edge debouncer: a burst of calls runs `fn` once, `ms` after the
 *  last call. `cancel()` drops a pending run (unmount). */
export function debounce(fn: () => void, ms: number): { (): void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, ms);
  };
  run.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return run;
}

/** `YYYY-MM-DD` in the browser's local time (what the day picker shows). */
export function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a `?date=YYYY-MM-DD` param as a LOCAL day; anything else (missing,
 *  malformed, in the future) falls back to today. */
export function parseDayParam(value: string | null, now: Date = new Date()): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return isoDay(now);
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (isoDay(date) !== value) return isoDay(now); // e.g. 2026-02-31
  return value > isoDay(now) ? isoDay(now) : value;
}

/** Shift a `YYYY-MM-DD` day by `delta` days (local calendar). */
export function shiftDay(day: string, delta: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return isoDay(new Date(y, m - 1, d + delta));
}
